import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useToast } from "../hooks/use-toast";
import { useSquarePayments } from "../hooks/useSquarePayments";
import { PersonalInfoSection } from "../components/checkout/PersonalInfoSection";
import { PaymentSection } from "../components/checkout/PaymentSection";
import { AgreementsSection } from "../components/checkout/AgreementsSection";
import { OrderSummary } from "../components/checkout/OrderSummary";
import { OrderSuccess } from "../components/checkout/OrderSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMO_CODES = {
  "KNIGHT10": { percent: 10, label: "10% off due today" },
  "WELCOME20": { percent: 20, label: "20% off due today" },
  "FIRSTWORK50": { amount: 50, label: "$50 off First Work Fee" },
};

const Checkout = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { items, removeItem, dueToday, monthly, clearCart } = useCart();
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "",
    cardholder: "",
    agreeTerms: false, agreePrivacy: false, agreeRetry: false, promo: false, biweekly: false,
    promoCode: "", promoApplied: null
  });

  const discount = (() => {
    const p = form.promoApplied;
    if (!p) return 0;
    if (p.percent) return Math.round(dueToday * (p.percent / 100) * 100) / 100;
    if (p.amount) return Math.min(p.amount, dueToday);
    return 0;
  })();
  const finalDueToday = Math.max(0, dueToday - discount);

  const {
    cardContainerRef,
    squareReady,
    squareError,
    applePayAvailable,
    googlePayAvailable,
    tokenizeCard,
    tokenizeWallet,
  } = useSquarePayments({ items, finalDueToday });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target?.value ?? e }));

  const detailsComplete = () =>
    form.firstName && form.lastName && form.email && form.phone && form.address && form.city && form.state && form.zip;

  const agreementsAccepted = () =>
    form.agreeTerms && form.agreePrivacy && form.agreeRetry && form.promo;

  const isValid = () => detailsComplete() && form.cardholder && squareReady && agreementsAccepted();

  const postOrderToBackend = async (sourceId, brand) => {
    const payload = {
      customer: {
        first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone,
        address: form.address, city: form.city, state: form.state, zip: form.zip,
      },
      items: items.map(i => ({ id: i.id, type: i.type, name: i.name, monthly: i.monthly || 0, setup_fee: i.setupFee || 0, one_time: i.oneTime || 0, qty: i.qty || 1 })),
      totals: { due_today: finalDueToday, monthly: monthly, original_due_today: dueToday, discount: discount },
      billing_preference: form.biweekly ? "biweekly" : "monthly",
      promo_code: form.promoApplied ? form.promoApplied.code : null,
      payment: { source_id: sourceId, last4: "****", brand },
    };
    const res = await axios.post(`${API}/checkout`, payload);
    setSuccess({ orderId: res.data.order_id, paymentId: res.data.payment_id, ...payload });
    clearCart();
  };

  // Apple Pay / Google Pay express checkout
  const handleWalletPay = async (which, label) => {
    if (!items.length) {
      toast({ title: "Cart is empty", description: "Add a plan before checking out.", variant: "destructive" });
      return;
    }
    if (!agreementsAccepted()) {
      toast({ title: "Please review the authorizations", description: "Scroll down and accept the four required authorizations before paying.", variant: "destructive" });
      return;
    }
    if (!detailsComplete()) {
      toast({ title: "Complete your details", description: "Please fill in your contact + billing address before paying.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const result = await tokenizeWallet(which);
      if (!result) {
        toast({ title: `${label} unavailable`, description: `${label} isn't available on this device right now.`, variant: "destructive" });
        setSubmitting(false);
        return;
      }
      if (result.status !== "OK") {
        const msg = (result.errors && result.errors[0] && result.errors[0].message) || `${label} was cancelled.`;
        toast({ title: `${label} ${result.status === "Cancel" ? "cancelled" : "failed"}`, description: msg, variant: "destructive" });
        setSubmitting(false);
        return;
      }
      await postOrderToBackend(result.token, label.toLowerCase().replace(/\s+/g, "_"));
    } catch (err) {
      const apiMsg = err?.response?.data?.detail;
      toast({ title: `${label} payment failed`, description: apiMsg || "Please try a different method.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const applyPromo = () => {
    const code = (form.promoCode || "").trim().toUpperCase();
    if (!code) {
      toast({ title: "Enter a code", description: "Type a promo code first.", variant: "destructive" });
      return;
    }
    const match = PROMO_CODES[code];
    if (!match) {
      setForm(f => ({ ...f, promoApplied: null }));
      toast({ title: "Invalid promo code", description: "That code isn't recognized.", variant: "destructive" });
      return;
    }
    setForm(f => ({ ...f, promoApplied: { code, ...match } }));
    toast({ title: "Promo applied!", description: `${code} — ${match.label}` });
  };

  // Card checkout
  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) {
      toast({ title: "Cart is empty", description: "Add a plan before checking out.", variant: "destructive" });
      return;
    }
    if (!isValid()) {
      toast({ title: "Please complete all required fields", description: "Don't forget the authorization & opt-in checkboxes.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const tokenResult = await tokenizeCard();
      if (tokenResult.status !== "OK") {
        const message = (tokenResult.errors && tokenResult.errors[0] && tokenResult.errors[0].message) ||
          "Please check your card details and try again.";
        toast({ title: "Card validation failed", description: message, variant: "destructive" });
        setSubmitting(false);
        return;
      }
      await postOrderToBackend(tokenResult.token, "card");
    } catch (err) {
      const apiMsg = err?.response?.data?.detail;
      toast({
        title: "Payment could not be processed",
        description: apiMsg || "Please try a different card or call 1 (888) 96-KNIGHT.",
        variant: "destructive"
      });
    }
    setSubmitting(false);
  };

  if (success) {
    return <OrderSuccess success={success} />;
  }

  return (
    <main className="py-12 bg-slate-50 min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-knight-navy/70 hover:text-knight-navy">
            <ArrowLeft size={16} /> Continue Shopping
          </button>
          <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
            <Lock size={14} /> Secure checkout
          </div>
        </div>

        <div className="mb-8">
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-1">Complete Your Order</h1>
          <p className="text-knight-navy/65">One step away from starting your credit journey.</p>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <PersonalInfoSection form={form} set={set} setForm={setForm} />
            <PaymentSection
              form={form}
              set={set}
              cardContainerRef={cardContainerRef}
              squareReady={squareReady}
              squareError={squareError}
              applePayAvailable={applePayAvailable}
              googlePayAvailable={googlePayAvailable}
              submitting={submitting}
              onWalletPay={handleWalletPay}
            />
            <AgreementsSection form={form} setForm={setForm} />
          </div>

          <OrderSummary
            items={items}
            removeItem={removeItem}
            dueToday={dueToday}
            monthly={monthly}
            finalDueToday={finalDueToday}
            discount={discount}
            form={form}
            setForm={setForm}
            applyPromo={applyPromo}
            submitting={submitting}
          />
        </form>
      </div>
    </main>
  );
};

export default Checkout;
